import React from 'react';
import { TouchableOpacity, View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/shared/useThemeColors';

interface GlassNavItemProps {
  title: string;
  icon: React.ReactNode;
  isActive: boolean;
  onPress: () => void;
}

export const GlassNavItem: React.FC<GlassNavItemProps> = React.memo(({
  title,
  icon,
  isActive,
  onPress,
}) => {
  const { colors } = useThemeColors();

  return (
    <TouchableOpacity
      style={[
        styles.container,
        { borderColor: colors.card.border, backgroundColor: colors.card.bg },
        isActive && { borderWidth: 1.5, borderColor: PRIMITIVE_COLORS.resioBlue, backgroundColor: colors.accent.blueBgMedium },
      ]}
      onPress={onPress}
      activeOpacity={0.7}
      accessibilityLabel={title}
      accessibilityHint={`Navigate to ${title}`}
      accessibilityRole="button"
      accessibilityState={{ selected: isActive }}
    >
      <View style={styles.iconContainer}>{icon}</View>
      <Text
        style={[
          styles.title,
          { color: isActive ? colors.text.primary : colors.text.muted },
          isActive && styles.titleActive,
        ]}
        numberOfLines={2}
      >
        {title}
      </Text>
    </TouchableOpacity>
  );
});

GlassNavItem.displayName = 'GlassNavItem';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderRadius: REDESIGN_SPACING.quickActionRadius,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  iconContainer: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.body,
    fontWeight: '500',
    flex: 1,
  },
  titleActive: {
    fontWeight: '600',
  },
});
